import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Sparkles, Music, Loader2 } from 'lucide-react';
import { dealerReels } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';

/**
 * CreateReelModal — opens from inventory rows; collects template + music choice,
 * fires off the background reel job, then navigates the dealer to the Reels list.
 */
const CreateReelModal = ({ open, onOpenChange, listing, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [musicTracks, setMusicTracks] = useState([]);
  const [picked, setPicked] = useState('premium_luxury');
  const [music, setMusic] = useState(null);
  const [usage, setUsage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    dealerReels.templates().then((r) => {
      setTemplates(r.templates || []);
      setMusicTracks(r.music_tracks || []);
    });
    dealerReels.usage().then(setUsage).catch(() => {});
    setPicked('premium_luxury');
    setMusic(null);
  }, [open]);

  const currentTpl = templates.find((t) => t.id === picked);

  const handleSubmit = async () => {
    if (!listing) return;
    setSubmitting(true);
    try {
      const res = await dealerReels.create({
        listing_id: listing.id,
        template: picked,
        music_track: music || null,
      });
      toast({ title: 'Reel queued', description: 'We\'ll render it in the background — this usually takes 1–2 minutes.' });
      onCreated && onCreated(res.reel);
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Failed to queue reel',
        description: err.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="create-reel-modal"
        className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            Generate Reel
          </DialogTitle>
        </DialogHeader>

        {/* Listing snapshot */}
        {listing && (
          <div className="bg-slate-950/50 border border-slate-700/50 rounded-xl p-3 flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg bg-slate-800 overflow-hidden flex-shrink-0">
              {listing.photos?.[0]?.url && (
                <img
                  src={listing.photos?.[0]?.enhanced_url || listing.photos?.[0]?.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="min-w-0">
              <div className="font-semibold truncate">
                {listing.year} {listing.ai_details?.make} {listing.ai_details?.model}
              </div>
              <div className="text-xs text-slate-400">
                ₹{(listing.price || 0).toLocaleString('en-IN')} · {listing.photos?.length || 0} photos
              </div>
            </div>
          </div>
        )}

        {/* Usage */}
        {usage && (
          <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center justify-between">
            <span>Monthly Quota</span>
            <span className="text-cyan-300 font-semibold">
              {usage.used} / {usage.limit} used · {usage.remaining} left
            </span>
          </div>
        )}

        {/* Templates grid */}
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-slate-400">Choose a style</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {templates.map((t) => {
              const active = t.id === picked;
              return (
                <button
                  key={t.id}
                  data-testid={`reel-template-${t.id}`}
                  onClick={() => setPicked(t.id)}
                  className={`text-left p-3 rounded-xl border transition-colors ${
                    active
                      ? 'bg-cyan-500/10 border-cyan-500/50'
                      : 'bg-slate-950/40 border-slate-700/50 hover:border-cyan-500/30'
                  }`}
                >
                  <div className="font-bold text-white">{t.label}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{t.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Music track */}
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <Music className="w-3.5 h-3.5" /> Background Music
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setMusic(null)}
              data-testid="reel-music-default"
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                !music
                  ? 'bg-cyan-500 text-slate-950 border-cyan-500'
                  : 'bg-slate-950/40 text-slate-300 border-slate-700 hover:border-cyan-500/30'
              }`}
            >
              Template default ({currentTpl?.default_music?.replace('.mp3', '') || 'auto'})
            </button>
            {musicTracks.map((m) => (
              <button
                key={m}
                onClick={() => setMusic(m)}
                data-testid={`reel-music-${m.replace('.mp3', '')}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  music === m
                    ? 'bg-cyan-500 text-slate-950 border-cyan-500'
                    : 'bg-slate-950/40 text-slate-300 border-slate-700 hover:border-cyan-500/30'
                }`}
              >
                {m.replace('.mp3', '').replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-700 bg-slate-900/40 text-slate-200"
            data-testid="reel-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (usage && usage.remaining <= 0)}
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
            data-testid="reel-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Reel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReelModal;
